import { Box } from "@mui/system";

import { useToast } from "@qubit/autoparts";
import { skipToken } from "@reduxjs/toolkit/query";
import { useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppSelector } from "~/app/store";
import { ErrorAlertBanner } from "~/features/testingTools/ErrorAlertBanner";
import {
  addEvent,
  selectBinCircusPortStatusById,
  setBinCircusPortRetrying
} from "~/features/testingTools/testingTools.slice";
import { getMessageFromRtkError } from "~/lib/rtkErrorToMessage";
import {
  selectThisWorkstation,
  selectWorkstationIsMultiPortLayout,
  selectWorkstationParentPortId
} from "~/redux/selectors/workstationsSelectors";

import { useGetPortStatusForBinCircusQuery } from "~/redux/warehouse/testingTools.hook";

import Port from "./Port";

type WorkstationPortInheritedProps = {
  portId: number;
  onRetryStartBinCircus: (portId: number) => Promise<void>;
  onRetryStopBinCircus: (portId: number) => Promise<void>;
};

const WorkstationPort = ({
  portId,
  onRetryStartBinCircus,
  onRetryStopBinCircus
}: WorkstationPortInheritedProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { errorToast } = useToast();

  const workstation = useAppSelector(selectThisWorkstation);
  const portStatusById = useAppSelector(selectBinCircusPortStatusById);
  const portStatus = portStatusById[portId] ?? null;
  const isBinCircusForPortRunning = portStatus?.portState === "cycling";
  const targetPorts = workstation?.ports.filter(
    (port) => port.parentPortId === portId || port.portId === portId
  );
  const parentPortId = useAppSelector(selectWorkstationParentPortId);
  const isMultiPortLayout = useAppSelector(selectWorkstationIsMultiPortLayout);
  const portError = portStatus?.error ?? null;

  const { isError: isBinCircusStatusError, error: binCircusStatusError } =
    useGetPortStatusForBinCircusQuery(
      workstation?.autostoreGridId && portId
        ? { gridId: workstation.autostoreGridId, portId }
        : skipToken,
      {
        refetchOnMountOrArgChange: true,
        refetchOnFocus: true,
        pollingInterval: isBinCircusForPortRunning ? 5000 : 0
      }
    );

  // Track if we've already logged this error to prevent duplicates during polling
  const hasLoggedErrorRef = useRef(false);

  useEffect(() => {
    if (!isBinCircusStatusError) {
      hasLoggedErrorRef.current = false;
      return;
    }

    if (
      isBinCircusStatusError &&
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
      isBinCircusForPortRunning &&
      !hasLoggedErrorRef.current
    ) {
      hasLoggedErrorRef.current = true;

      const binCircusStatusErrorMessage =
        getMessageFromRtkError(binCircusStatusError);

      errorToast(binCircusStatusErrorMessage, {
        duration: 2000
      });

      dispatch(
        addEvent({
          text: t("failed to sync bin circus status for port {{portId}}", {
            portId
          }),
          state: "Failed",
          testOrigin: "Bin to Port"
        })
      );
    }
  }, [
    isBinCircusStatusError,
    isBinCircusForPortRunning,
    portId,
    dispatch,
    binCircusStatusError,
    errorToast,
    t
  ]);

  const retryBinCircusAction = useCallback(async () => {
    if (!portError) return;

    dispatch(setBinCircusPortRetrying(portId));

    if (portError.type === "start") {
      await onRetryStartBinCircus(portId);
    } else if (portError.type === "stop") {
      await onRetryStopBinCircus(portId);
    }
  }, [
    dispatch,
    portError,
    portId,
    onRetryStartBinCircus,
    onRetryStopBinCircus
  ]);

  return (
    <Box
      sx={{
        ...(isMultiPortLayout
          ? {
              display: "grid",
              gridTemplateColumns: `repeat(${targetPorts?.length}, minmax(250px, 350px))`,
              width: "100%",
              minWidth: 0,
              gap: 2,
              justifyContent: "center"
            }
          : {}),
        gridRow: parentPortId !== null ? "2/3" : "auto"
      }}
    >
      {targetPorts?.map((p, i) => (
        <Port
          key={p.portId}
          portSideIndex={i}
          portIdToDisplay={portId === parentPortId ? p.portId : portId}
          portId={portId}
        />
      ))}
      {portError && (
        <Box sx={{ ...(isMultiPortLayout && { gridColumn: "1 / -1" }) }}>
          <ErrorAlertBanner
            title={
              portError.type === "start"
                ? t("failed to start bin circus")
                : t("failed to stop bin circus")
            }
            message={portError.message}
            onRetry={retryBinCircusAction}
            isRetryActionLoading={portError.isRetrying}
          />
        </Box>
      )}
    </Box>
  );
};

export default WorkstationPort;
